import { FC, useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { t } from 'i18next';
import { Button } from '@/components/ui/button';
import NotificationIcon from '@/components/icons/NotificationIcon';
import { useNavigate } from 'react-router-dom';
import TickDouble_02 from '@/components/icons/TickDouble_02';
import useEmblaCarousel from 'embla-carousel-react';
import { Roles } from '@/@types/auth.types';
import { useNotificationsListApi } from '@/features/notifications/hooks/useNotificationsListApi';
import { NotificationsUtils } from '@/features/notifications/utils/notificationsUtils';
import {
	NotificationCategory,
	NotificationsBarButtons,
} from '@/features/notifications/types/notifications-dto';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useNotificationDetailsApi } from '@/features/notifications/hooks/useNotificationDetailsApi';

type Props = {};

const NotificationsBar: FC<Props> = ({}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeButton, setActiveButton] = useState(
		NotificationsBarButtons.All,
	);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const navigate = useNavigate();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const { ownerNotificationsListData } = useNotificationsListApi({
		role: Roles.owner,
		isRead: false,
	});

	const { readAllNotification, isReadingAllNotification } =
		useNotificationDetailsApi();

	if (!ownerNotificationsListData) return null;

	const handleOpenToggle = () => {
		setIsOpen((prev) => !prev);
	};

	const handleNavigate = () => {
		navigate('/notifications');
		handleOpenToggle();
	};

	const handleClickButton = (index: number) => {
		setActiveButton(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const handleReadAll = async () => {
		await readAllNotification();
	};

	const NotificationsBarButtonsList = [
		{
			name: t('main.notifications.components.bar.buttons.all'),
			quantity: NotificationsUtils.getNotificationsListLength(
				ownerNotificationsListData,
			),
		},
		{
			name: t('main.notifications.components.bar.buttons.admin'),
			quantity: NotificationsUtils.getNotificationsListLength(
				ownerNotificationsListData,
				NotificationCategory.Admin,
			),
		},
		{
			name: t('main.notifications.components.bar.buttons.finances'),
			quantity: NotificationsUtils.getNotificationsListLength(
				ownerNotificationsListData,
				NotificationCategory.Finances,
			),
		},
		{
			name: t('main.notifications.components.bar.buttons.calendar'),
			quantity: NotificationsUtils.getNotificationsListLength(
				ownerNotificationsListData,
				NotificationCategory.Calendar,
			),
		},
		{
			name: t('main.notifications.components.bar.buttons.orders'),
			quantity: NotificationsUtils.getNotificationsListLength(
				ownerNotificationsListData,
				NotificationCategory.Orders,
			),
		},
	];

	const sortedData = NotificationsUtils.filterNotificationsListByCategory(
		ownerNotificationsListData,
		activeButton === NotificationsBarButtons.All
			? undefined
			: NotificationsUtils.notificationBarCategoryMap[activeButton],
	);

	return (
		<DropdownMenu
			open={isOpen}
			onOpenChange={handleOpenToggle}>
			<DropdownMenuTrigger asChild>
				<div className='relative'>
					{!!NotificationsBarButtonsList[0].quantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{NotificationsBarButtonsList[0].quantity}
						</div>
					)}
					<Button
						variant='outline'
						size='icon'>
						<NotificationIcon className='stroke-popover-1000' />
					</Button>
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align='end'
				className={
					'w-90 p-6 flex flex-col rounded-2.5xl border border-popover-100 bg-popover-10 shadow-custom'
				}>
				<div className='-mx-6 px-6 pb-6 flex flex-col gap-4 border-b border-popover-100'>
					<div className='flex items-center justify-between gap-4'>
						<h4 className='text-lg font-medium text-popover-1000'>
							{t('main.notifications.components.bar.title')}
						</h4>
						{!!NotificationsBarButtonsList[0].quantity && (
							<Button
								variant='link'
								className='p-0'
								onClick={handleReadAll}
								disabled={isReadingAllNotification}>
								<TickDouble_02 />
								{t('elements.buttons.markAsRead')}
							</Button>
						)}
					</div>
					<div
						className={cn(
							'-mx-6 px-6 overflow-hidden',
							isMobile && '-mx-4 px-4',
						)}
						ref={emblaRef}>
						<div className='flex gap-2 touch-pan-y'>
							{NotificationsBarButtonsList.map((btn, index) => (
								<Button
									key={index}
									variant={
										activeButton === index
											? 'default'
											: 'outline'
									}
									className={cn(
										`h-9 px-3.5 py-2 hover:shadow-none ${
											activeButton === index
												? ''
												: 'hover:bg-popover-50'
										} `,
									)}
									onClick={() => handleClickButton(index)}>
									{btn.name}
									{!!btn.quantity && (
										<div className='size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10'>
											{btn.quantity}
										</div>
									)}
								</Button>
							))}
						</div>
					</div>
				</div>
				<div
					className={cn(
						'h-80 -mx-6 px-6 py-6 flex flex-col gap-6 overflow-y-auto',
						isMobile && 'gap-4',
					)}>
					{sortedData?.map(
						([date, notifications]) =>
							!!notifications.length && (
								<div key={`${date}`}>
									<h5 className='text-sm text-popover-800'>
										{date}
									</h5>
									{notifications?.map((notification) => {
										const { label, icon, iconColors } =
											NotificationsUtils.getNotificationsConfig(
												notification.category,
											);

										const header = `${label} ${
											notification.isRequired
												? ''
												: '(optional)'
										}`;

										return (
											<EActivitiesElement
												key={notification.id}
												icon={icon}
												colorClass={iconColors}
												header={header}
												description={
													notification.title ??
													t(
														'main.notifications.components.bar.noTitle',
													)
												}
												value={DateTimeHelper.formatTime(
													notification.sent,
												)}
												id={notification.id}
												isMobile={isMobile}
												headerClassName='text-sm text-popover-600'
												descriptionClassName='text-popover-1000'
											/>
										);
									})}
								</div>
							),
					)}
				</div>
				<div className='-mx-6 px-6 pt-6 border-t border-popover-100 text-center'>
					<Button
						variant={'link'}
						className='p-0 font-medium'
						onClick={handleNavigate}>
						{t('main.notifications.components.bar.seeMoreButton')}
					</Button>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default NotificationsBar;
